<template>
  <nav v-if="pageBlocks.length > 1" class="blog-pagination">
    <div class="-mt-px flex w-0 flex-1">
      <button class="step-button" @click="setPreviousPage">
        {{ $t('previous') }}
      </button>
    </div>
    <div class="hidden md:-mt-px md:flex">
      <template v-for="page in pageBlocks" :key="page">
        <button
          class="page-button"
          :class="page === currentPage && 'is-active'"
          :disabled="typeof page === 'string'"
          @click="setCurrentPage(page)"
        >
          {{ page }}
        </button>
      </template>
    </div>
    <div class="-mt-px flex w-0 flex-1 justify-end">
      <button class="step-button justify-end" @click="setNextPageValue">
        {{ $t('next') }}
      </button>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { isNumber } from 'lodash-es';
import { computed, defineModel } from 'vue';
import { usePagination } from '~/composables';

interface Props {
  perPage: number
  currentPage: number
  totalItems: number
}

const props = defineProps<Props>();
const currentPageModel = defineModel<Props['currentPage']>('currentPage', { default: 1 });
const { calculateTotalPages, generatePageBlocks } = usePagination();
const totalPages = computed(() => calculateTotalPages(props.totalItems, props.perPage));
const pageBlocks = computed(() => {
  return generatePageBlocks({
    currentPage: currentPageModel.value,
    totalPages: totalPages.value,
    maxPagesVisible: 9,
  });
});

function setCurrentPage (page: number | string) {
  if (!isNumber(page)) return;

  currentPageModel.value = page;
}

function setPreviousPage () {
  if (currentPageModel.value > 1) {
    currentPageModel.value -= 1;
  }
}

function setNextPageValue () {
  if (currentPageModel.value < totalPages.value) {
    currentPageModel.value += 1;
  }
}
</script>

<style scoped>
.blog-pagination {
  @apply flex items-center justify-between border-t border-gray-200 px-4 sm:px-0;
  .page-button {
    @apply inline-flex items-center border-t-2 px-4 pt-4 text-sm font-medium select-none;
    &:not(.is-active) {
      @apply text-brand-black/60 hover:border-gray-300 hover:text-brand-black/50 border-transparent;
    }
    &.is-active {
      @apply border-brand-primary text-brand-primary;
    }
  }
  .step-button {
    @apply inline-flex items-center border-t-2 border-transparent pl-1 pt-4 text-sm font-medium min-w-24 select-none capitalize;
    @apply text-brand-black/60 hover:border-gray-300 hover:text-brand-black/50;
  }
}
</style>
